/**
 * API client for backend communication
 * Provides typed fetch wrappers for the cost calculation API
 */

import { BoxSpecifications, SkuItem, GangingAnalysis } from '../types';
import { PricingData } from './PricingContext';
import { fetchWithInterceptors } from './apiInterceptors';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_TIMEOUT = 30000; // 30 seconds

// Simple API client for billing endpoints
export const api = {
  get: async (endpoint: string) => {
    const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw error;
    }
    
    return { data: await response.json() };
  },
  
  post: async (endpoint: string, data?: unknown) => {
    const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw error;
    }
    
    return { data: await response.json() };
  },
  
  put: async (endpoint: string, data?: unknown) => {
    const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw error;
    }
    
    return { data: await response.json() };
  },
  
  delete: async (endpoint: string) => {
    const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw error;
    }
    
    return { data: await response.json() };
  },
};

// Response types from backend
export interface ApiError {
  success: false;
  error: {
    type: string;
    message: string;
    details?: {
      field?: string;
      code?: string;
      message?: string;
      providedValue?: unknown;
      reason?: string;
      errorMessage?: string;
      processingTime?: string;
    };
  };
  metadata: {
    timestamp: string;
    requestId: string;
    endpoint: string;
    errorId?: string;
  };
}

export interface CostBreakdownResponse {
  materialCost: number;
  sheetWastageCost: number;
  printingCost: number;
  scanningCost: number;
  platesCost: number;
  dieCost: number;
  dieCuttingCost: number;
  pastingCost: number;
  additionalFeaturesCost: number;
  laminationCost: number;
  laminationWastageCost: number;
  insideLaminationCost: number;
  insideLaminationWastageCost: number;
  weight: number;
  shippingBoxWeight: number;
  totalWeight: number;
  numberOfShippingBoxes: number;
  shippingCost: number;
  productionSubtotal: number;
  total: number;
  costPerUnit: number;
  discount: number;
  standardSheetSize: { width: number; height: number };
  materialUtilization: number;
  boxesPerSheet: number;
  isUpped: boolean;
  openDimensions: {
    openLength: number;
    openWidth: number;
  };
  selectedPlateSize?: string; // Selected plate size for printing (e.g., '18x25')
  billingInfo?: {
    remaining_quotes: number;
    draft_expires_at?: string | null;
  };
}

export interface Warning {
  type: string;
  severity: 'INFO' | 'WARNING' | 'ERROR';
  message: string;
  code: string;
  details?: Record<string, unknown>;
}

export interface ApiSuccess {
  success: true;
  data: {
    costBreakdown: CostBreakdownResponse;
    warnings: Warning[];
    metadata: {
      processingTime: string;
      pricingVersion: string;
      calculatedAt: string;
      requestId: string;
    };
  };
}

export interface QuoteDraftResponse {
  id: string;
  companyId: string;
  userId: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  promotedAt?: string | null;
  expiresAt?: string | null;
  savedQuoteId?: string | null;
  boxType?: string | null;
  material?: string | null;
  quantity?: number | null;
  totalCost?: number | null;
  vendorProfit?: number | null;
  dimensions?: Record<string, unknown> | null;
  calculation?: Record<string, unknown>;
}

export type ApiResponse = ApiSuccess | ApiError;

// Dimension calculation response type
export interface CalculateDimensionsResponse {
  success: true;
  data: {
    openDimensions: {
      openLength: number;
      openWidth: number;
    };
    input: {
      boxType: string;
      length: number;
      width: number;
      height: number;
    };
    calculatorUsed: 'dynamic' | 'static';
  };
  metadata: {
    processingTime: string;
    timestamp: string;
    requestId: string;
    endpoint: string;
  };
}

// Custom error classes
export class ApiTimeoutError extends Error {
  constructor(message = 'Request timeout') {
    super(message);
    this.name = 'ApiTimeoutError';
  }
}

export class NetworkError extends Error {
  constructor(message = 'Network error') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class ApiValidationError extends Error {
  public field?: string;
  public code?: string;
  public providedValue?: unknown;

  constructor(message: string, field?: string, code?: string, providedValue?: unknown) {
    super(message);
    this.name = 'ApiValidationError';
    this.field = field;
    this.code = code;
    this.providedValue = providedValue;
  }
}

export class ApiServerError extends Error {
  public code: string;
  public details?: Record<string, unknown>;

  constructor(message: string, code: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiServerError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Create a timeout promise that rejects after specified milliseconds
 */
function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new ApiTimeoutError(`Request timeout after ${ms}ms`)), ms);
  });
}

/**
 * Transform frontend BoxSpecifications to backend API format
 */
function transformBoxSpecifications(
  specs: BoxSpecifications,
  openDimensions?: { openLength: number; openWidth: number },
  adminMargins?: { manufacturerProfit: number; companyMargin: number }
): Record<string, unknown> {
  // Determine effective GSM to ensure backend weight calculation always has a value
  let effectiveGsm: number | undefined = specs.gsm as number | undefined;
  if (specs.material === 'corrugated') {
    const corrugatedGSMByVariant: Record<string, number> = {
      'single-kraft': 250,
      'single-cardboard': 270,
      'double-kraft': 350,
      'double-cardboard': 380
    };
    if (specs.corrugatedType && corrugatedGSMByVariant[specs.corrugatedType]) {
      effectiveGsm = corrugatedGSMByVariant[specs.corrugatedType];
    }
  }

  const baseRequest: Record<string, unknown> = {
    boxType: specs.boxType,
    material: specs.material,
    // Always send a GSM so the backend never produces NaN for weight
    gsm: effectiveGsm,
    corrugatedType: specs.material === 'corrugated' ? specs.corrugatedType : undefined,
    quantity: specs.quantity,
    printingColors: {
      outside: {
        cmyk: specs.printing?.cmyk || 0,
        pantone: specs.printing?.pantone || 0,
        metallic: specs.printing?.metallic || 0
      },
      inside: {
        cmyk: specs.insidePrinting?.cmyk || 0,
        pantone: specs.insidePrinting?.pantone || 0,
        metallic: specs.insidePrinting?.metallic || 0
      }
    },
    features: specs.features || [],
    featureAreas: specs.featureAreas || {},
    coatingType: specs.coatingType,
    laminationBothSides: specs.laminationBothSides || false,
    // For admin users, send both manufacturer and company margins separately
    // For agents/users, send vendorProfit as before
    vendorProfit: adminMargins ? adminMargins.manufacturerProfit : specs.vendorProfit,
    // Add admin-specific margin fields
    manufacturerProfit: adminMargins ? adminMargins.manufacturerProfit : undefined,
    companyMargin: adminMargins ? adminMargins.companyMargin : undefined,
    shippingOption: specs.shippingOption || 'UPS',
    shippingCountry: specs.shippingCountry || 'USA',
    shippingType: specs.shippingType || 'express'
  };

  // Add dimensions based on mode
  // Check for openDimensions parameter OR specs.openLength/openWidth properties
  const hasOpenDimensions = openDimensions ||
    ((specs as Record<string, unknown>).openLength !== undefined &&
     (specs as Record<string, unknown>).openWidth !== undefined);

  if (hasOpenDimensions) {
    // Open size mode - send open dimensions directly
    const openLength = openDimensions?.openLength ?? (specs as Record<string, unknown>).openLength as number;
    const openWidth = openDimensions?.openWidth ?? (specs as Record<string, unknown>).openWidth as number;
    baseRequest.openLength = openLength;
    baseRequest.openWidth = openWidth;
  } else {
    // Dimensions mode - send box dimensions
    baseRequest.length = specs.length;
    baseRequest.width = specs.width;
    baseRequest.height = specs.height;
  }

  // Add insert specification if enabled
  if ((specs as Record<string, unknown>).insert) {
    baseRequest.insert = (specs as Record<string, unknown>).insert;
  }

  return baseRequest;
}

/**
 * Make a fetch request with timeout and error handling
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout = API_TIMEOUT
): Promise<Response> {
  try {
    // Build headers without auto-injecting mock auth here; interceptors handle auth
    const baseRequest: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const response = await Promise.race([
      fetchWithInterceptors(url, baseRequest),
      createTimeout(timeout)
    ]);

    return response;
  } catch (error) {
    if (error instanceof ApiTimeoutError) {
      throw error;
    }
    
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new NetworkError('Failed to connect to server. Please check your connection.');
    }
    
    throw error;
  }
}

/**
 * Parse and handle API response
 */
async function handleResponse(response: Response): Promise<ApiResponse> {
  let responseData: unknown;
  
  try {
    responseData = await response.json();
  } catch (error) {
    throw new ApiServerError(
      'Invalid response format from server',
      'INVALID_RESPONSE',
      { status: response.status, statusText: response.statusText }
    );
  }

  // Handle successful responses
  if (response.ok && responseData.success) {
    return responseData as ApiSuccess;
  }

  // Handle API error responses
  if (!responseData.success && responseData.error) {
    const errorResponse = responseData as ApiError;
    
    // Throw specific error types based on error type
    switch (errorResponse.error.type) {
      case 'VALIDATION_ERROR':
        throw new ApiValidationError(
          errorResponse.error.details?.message || errorResponse.error.message,
          errorResponse.error.details?.field,
          errorResponse.error.details?.code,
          errorResponse.error.details?.providedValue
        );
      
      case 'CONFIG_ERROR':
      case 'CALCULATION_ERROR':
        throw new ApiServerError(
          errorResponse.error.message,
          errorResponse.error.type,
          errorResponse.error.details
        );
      
      default:
        throw new ApiServerError(
          errorResponse.error.message || 'Unknown server error',
          errorResponse.error.type || 'UNKNOWN_ERROR',
          errorResponse.error.details
        );
    }
  }

  // Handle unexpected response format
  throw new ApiServerError(
    `Server returned ${response.status}: ${response.statusText}`,
    'HTTP_ERROR',
    { status: response.status, statusText: response.statusText, body: responseData }
  );
}

/**
 * Calculate quote from backend API
 */
export async function calculateQuote(
  specifications: BoxSpecifications,
  openDimensions?: { openLength: number; openWidth: number },
  adminMargins?: { manufacturerProfit: number; companyMargin: number }
): Promise<ApiSuccess> {
  const url = `${API_BASE_URL}/api/v1/calculate-quote`;
  const requestBody = transformBoxSpecifications(specifications, openDimensions, adminMargins);

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  const result = await handleResponse(response);

  // Type guard to ensure we return ApiSuccess
  if (result.success) {
    return result;
  }

  // This should not happen due to error handling above, but TypeScript safety
  throw new ApiServerError('Unexpected response format', 'UNEXPECTED_RESPONSE');
}

/**
 * Bulk calculate quote for multiple quantities in one request
 */
export async function bulkCalculateQuote(
  specifications: BoxSpecifications,
  quantities: number[],
  openDimensions?: { openLength: number; openWidth: number },
  adminMargins?: { manufacturerProfit: number; companyMargin: number }
): Promise<
  {
    success: true;
    data: {
      variants: Array<{ quantity: number; costBreakdown: CostBreakdownResponse }>;
      billingInfo: { remaining_quotes: number; draft_expires_at?: string | null } | null;
      metadata: Record<string, unknown>;
    };
  }
  | ApiError
> {
  const url = `${API_BASE_URL}/api/v1/calculate-quote/bulk`;
  const baseRequest = transformBoxSpecifications(specifications, openDimensions, adminMargins);
  const body = { ...baseRequest, quantities };

  try {
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const result = await handleResponse(response);
    return result;
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'BULK_CALCULATION_ERROR',
        message: getErrorMessage(error)
      },
      metadata: {
        timestamp: new Date().toISOString(),
        requestId: 'bulk_' + Date.now(),
        endpoint: '/api/v1/calculate-quote/bulk'
      }
    };
  }
}

/**
 * Multi-SKU quote calculation response
 */
export interface MultiSkuQuoteResponse {
  success: true;
  data: {
    costBreakdown: CostBreakdownResponse & {
      gangingAnalysis: GangingAnalysis;
      gangingWastageCost?: number;
    };
    allShippingOptions: Record<string, unknown>;
    warnings: Array<{ type: string; message: string }>;
    metadata: {
      processingTime: string;
      pricingVersion: string;
      calculatedAt: string;
      skuCount: number;
      strategy: 'ganged' | 'separate';
    };
  };
}

/**
 * Calculate quote for multi-SKU orders (multiple designs of same box size)
 * Automatically optimizes between ganged and separate production
 */
export async function calculateMultiSkuQuote(
  specifications: Omit<BoxSpecifications, 'quantity'>,
  skus: SkuItem[],
  openDimensions?: { openLength: number; openWidth: number },
  adminMargins?: { manufacturerProfit: number; companyMargin: number }
): Promise<MultiSkuQuoteResponse | ApiError> {
  const url = `${API_BASE_URL}/api/v1/calculate-quote/multi-sku`;

  // Build request from base specifications (without quantity)
  const baseRequest = transformBoxSpecifications(
    { ...specifications, quantity: 1 } as BoxSpecifications, // Dummy quantity for transform
    openDimensions,
    adminMargins
  );

  // Remove quantity from request (it comes from SKUs)
  delete (baseRequest as Record<string, unknown>).quantity;

  const body = { ...baseRequest, skus };

  try {
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const result = await handleResponse(response);

    if (result.success) {
      return result as MultiSkuQuoteResponse;
    }

    return result as ApiError;
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'MULTI_SKU_CALCULATION_ERROR',
        message: getErrorMessage(error)
      },
      metadata: {
        timestamp: new Date().toISOString(),
        requestId: 'multisku_' + Date.now(),
        endpoint: '/api/v1/calculate-quote/multi-sku'
      }
    };
  }
}

/**
 * Multi-Quantity quote calculation response
 * For comparing different quantity tiers of the same design
 */
export interface QuantityTier {
  quantity: number;
  weight: number;
  costBreakdown: CostBreakdownResponse;
  allShippingOptions: {
    express: Record<string, { option: string; destination: string; cost: number; rate: number; weight: number }>;
    airFreight: Record<string, { option: string; cost: number; rate: number; weight: number }>;
    seaFreight: Record<string, { option: string; cost: number; rate: number; weight: number }>;
  };
  bestShipping: {
    type: 'express' | 'airFreight' | 'seaFreight';
    option: string;
    cost: number;
    key: string;
  } | null;
  savingsPercent: number;
}

export interface ShippingThresholdSuggestion {
  type: 'airFreight' | 'seaFreight';
  threshold: number;
  unitsNeeded: number;
  message: string;
}

export interface MultiQuantityQuoteResponse {
  success: true;
  data: {
    tiers: QuantityTier[];
    suggestions: ShippingThresholdSuggestion[];
    metadata: {
      processingTime: string;
      pricingVersion: string;
      calculatedAt: string;
      quantityCount: number;
      destination: string;
    };
  };
}

/**
 * Calculate quote for multiple quantity tiers of the same design
 * Each tier gets its own shipping options based on weight
 */
export async function calculateMultiQuantityQuote(
  specifications: Omit<BoxSpecifications, 'quantity'>,
  quantities: number[],
  openDimensions?: { openLength: number; openWidth: number },
  adminMargins?: { manufacturerProfit: number; companyMargin: number }
): Promise<MultiQuantityQuoteResponse | ApiError> {
  const url = `${API_BASE_URL}/api/v1/calculate-quote/multi-quantity`;

  // Build request from base specifications (without quantity)
  const baseRequest = transformBoxSpecifications(
    { ...specifications, quantity: 1 } as BoxSpecifications, // Dummy quantity for transform
    openDimensions,
    adminMargins
  );

  // Remove quantity from request (it comes from quantities array)
  delete (baseRequest as Record<string, unknown>).quantity;

  const body = { ...baseRequest, quantities };

  try {
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const result = await handleResponse(response);

    if (result.success) {
      return result as MultiQuantityQuoteResponse;
    }

    return result as ApiError;
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'MULTI_QUANTITY_CALCULATION_ERROR',
        message: getErrorMessage(error)
      },
      metadata: {
        timestamp: new Date().toISOString(),
        requestId: 'multiqty_' + Date.now(),
        endpoint: '/api/v1/calculate-quote/multi-quantity'
      }
    };
  }
}

/**
 * Calculate open dimensions from backend API (lightweight preview calculation)
 */
export async function calculateDimensions(
  boxType: string,
  length: number,
  width: number,
  height: number
): Promise<CalculateDimensionsResponse> {
  const url = `${API_BASE_URL}/api/v1/calculate-dimensions`;
  const requestBody = {
    boxType,
    length,
    width,
    height
  };

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  const result = await handleResponse(response);

  // Type guard to ensure we return CalculateDimensionsResponse
  if (result.success && result.data?.openDimensions) {
    return result as CalculateDimensionsResponse;
  }

  // This should not happen due to error handling above, but TypeScript safety
  throw new ApiServerError('Unexpected response format', 'UNEXPECTED_RESPONSE');
}

/**
 * Health check endpoint
 */
export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  const url = `${API_BASE_URL}/api/health`;
  
  try {
    const response = await fetchWithTimeout(url, { method: 'GET' }, 5000);
    
    if (!response.ok) {
      throw new ApiServerError(`Health check failed: ${response.statusText}`, 'HEALTH_CHECK_FAILED');
    }
    
    return await response.json();
  } catch (error) {
    if (error instanceof ApiTimeoutError || error instanceof NetworkError) {
      throw error;
    }
    throw new ApiServerError('Health check failed', 'HEALTH_CHECK_ERROR');
  }
}

/**
 * Check pricing configuration status
 */
export async function pricingHealthCheck(): Promise<unknown> {
  const url = `${API_BASE_URL}/api/pricing/health`;
  
  const response = await fetchWithTimeout(url, { method: 'GET' }, 5000);
  
  if (!response.ok) {
    throw new ApiServerError(`Pricing health check failed: ${response.statusText}`, 'PRICING_HEALTH_FAILED');
  }
  
  return await response.json();
}

/**
 * Utility function to check if error is a specific type
 */
export function isApiError(error: unknown, type?: string): error is ApiValidationError | ApiServerError {
  if (error instanceof ApiValidationError || error instanceof ApiServerError) {
    return type ? error.name === type : true;
  }
  return false;
}

/**
 * Utility function to extract user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiValidationError) {
    return error.message;
  }
  
  if (error instanceof ApiServerError) {
    return error.message;
  }
  
  if (error instanceof ApiTimeoutError) {
    return 'Request timeout. Please try again.';
  }
  
  if (error instanceof NetworkError) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Fetch pricing configuration from backend
 */
export async function fetchPricingConfig(): Promise<{ success: boolean; data?: PricingData; error?: string }> {
  const url = `${API_BASE_URL}/api/config/pricing`;
  
  try {
    const response = await fetchWithTimeout(url, { method: 'GET' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error?.message || `Failed to fetch pricing: ${response.statusText}`
      };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error)
    };
  }
}

/**
 * Update pricing configuration in backend
 */
export async function updatePricingConfig(updates: Partial<PricingData>): Promise<{ success: boolean; data?: PricingData; error?: string }> {
  const url = `${API_BASE_URL}/api/config/pricing`;
  
  try {
    const response = await fetchWithTimeout(url, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error?.message || `Failed to update pricing: ${response.statusText}`
      };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error)
    };
  }
}

/**
 * Fetch quotes from backend API
 */
export async function fetchQuotes(params?: { from?: string; to?: string }): Promise<{ success: boolean; data?: { quotes: Array<Record<string, unknown>> }; error?: string }> {
  // Build query params
  const searchParams = new URLSearchParams();
  if (params?.from) searchParams.append('from', params.from);
  if (params?.to) searchParams.append('to', params.to);
  
  const url = `${API_BASE_URL}/api/v1/quotes${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  
  try {
    const response = await fetchWithTimeout(url, { method: 'GET' });
    
    if (!response.ok) {
      // Handle authentication errors specifically
      if (response.status === 401) {
        throw new Error('Authentication required');
      }
      
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error?.message || `Failed to fetch quotes: ${response.statusText}`
      };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error)
    };
  }
}

/**
 * Fetch recent quote drafts from backend API
 */
export async function fetchRecentQuoteDrafts(params?: { limit?: number; offset?: number; includeExpired?: boolean }): Promise<
  { success: true; data: { drafts: QuoteDraftResponse[]; pagination: { limit: number; offset: number; count: number } } } | { success: false; error: string }
> {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.append('limit', String(params.limit));
  if (params?.offset != null) searchParams.append('offset', String(params.offset));
  if (params?.includeExpired) searchParams.append('includeExpired', 'true');

  const url = `${API_BASE_URL}/api/v1/quotes/recent${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  try {
    const response = await fetchWithTimeout(url, { method: 'GET' });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required');
      }
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error?.message || `Failed to fetch recent quotes: ${response.statusText}`,
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
}

/**
 * Save a quote to backend API
 */
export async function saveQuote(quoteData: Record<string, unknown>): Promise<{ success: boolean; data?: { id: string }; error?: string }> {
  const url = `${API_BASE_URL}/api/v1/quotes`;
  
  try {
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      body: JSON.stringify(quoteData),
    });
    
    if (!response.ok) {
      // Handle authentication errors specifically
      if (response.status === 401) {
        throw new Error('Authentication required');
      }
      
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error?.details?.message || errorData.error?.message || `Failed to save quote: ${response.statusText}`
      };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error)
    };
  }
}

/**
 * Fetch a specific quote by ID from backend API
 */
export async function fetchQuoteById(quoteId: string): Promise<{ success: boolean; data?: Record<string, unknown>; error?: string }> {
  const url = `${API_BASE_URL}/api/v1/quotes/${quoteId}`;
  
  try {
    const response = await fetchWithTimeout(url, { method: 'GET' });
    
    if (!response.ok) {
      // Handle authentication errors specifically
      if (response.status === 401) {
        throw new Error('Authentication required');
      }
      
      // Handle not found errors
      if (response.status === 404) {
        throw new Error('Quote not found or you do not have permission to view it');
      }
      
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error?.message || `Failed to fetch quote: ${response.statusText}`
      };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error)
    };
  }
}

// Error types are already exported above when defined
